import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth0 } from "@auth0/auth0-react";
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { FaCreditCard, FaPaypal, FaMobile, FaUniversity, FaLink, FaToggleOn, FaToggleOff } from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MasterToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  margin-bottom: 8px;
`;

const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToggleTitle = styled.span`
  font-weight: 600;
  color: #fff;
  font-size: 15px;
`;

const ToggleDescription = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  cursor: pointer;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.1);
    transition: 0.3s;
    border-radius: 26px;
    
    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 3px;
      bottom: 3px;
      background-color: #fff;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }
  
  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const PaymentMethodCard = styled.div`
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%);
  border: 1px solid ${props => props.$enabled ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const PaymentMethodHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const PaymentMethodInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaymentMethodIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bg || 'rgba(99, 102, 241, 0.2)'};
  color: ${props => props.$color || '#6366f1'};
  font-size: 18px;
`;

const PaymentMethodName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MethodTitle = styled.span`
  font-weight: 600;
  color: #fff;
  font-size: 15px;
`;

const MethodDescription = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const PaymentMethodBody = styled.div`
  padding: 16px 20px;
  display: ${props => props.$show ? 'flex' : 'none'};
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin-top: 8px;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Select = styled.select`
  padding: 10px 12px;
  background: rgba(15, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  
  option {
    background: #1a1f2e;
  }
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  background: ${props => props.$error 
    ? 'rgba(239, 68, 68, 0.1)' 
    : 'rgba(16, 185, 129, 0.1)'};
  border: 1px solid ${props => props.$error 
    ? 'rgba(239, 68, 68, 0.3)' 
    : 'rgba(16, 185, 129, 0.3)'};
  color: ${props => props.$error ? '#ef4444' : '#10b981'};
`;

const HelpText = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
`;

// Default payment settings
const defaultSettings = {
  enabled: false,
  defaultCurrency: 'USD',
  defaultExpirationDays: 7,
  sendReminders: true,
  reminderDays: [3, 1],
  paypal: { enabled: false, paypalEmail: '' },
  bit: { enabled: false, phoneNumber: '' },
  bankTransfer: { 
    enabled: false, 
    bankName: '', 
    accountName: '', 
    accountNumber: '', 
    routingNumber: '',
    swiftCode: '',
    bankNotes: ''
  },
  customLink: { enabled: false, customUrl: '', customLabel: '' }
};

const PaymentSettings = ({ onSettingsChange }) => {
  const { user } = useAuth0();
  const axiosInstance = useAuthenticatedAxios();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', error: false });

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [user?.sub]);

  const loadSettings = async () => {
    if (!user?.sub) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/client-payments/settings/${user.sub}`);
      if (response.data.paymentSettings) {
        setSettings(prev => ({ ...prev, ...response.data.paymentSettings }));
      }
    } catch (error) {
      console.error('Failed to load payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.sub) return;
    
    try {
      setSaving(true);
      setMessage({ text: '', error: false });
      
      await axiosInstance.put(`/client-payments/settings/${user.sub}`, {
        paymentSettings: settings
      });
      
      setMessage({ text: 'Payment settings saved successfully!', error: false });
      if (onSettingsChange) onSettingsChange(settings);
      
      setTimeout(() => setMessage({ text: '', error: false }), 3000);
    } catch (error) {
      console.error('Failed to save payment settings:', error);
      setMessage({ text: 'Failed to save settings. Please try again.', error: true });
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updatePaymentMethod = (method, key, value) => {
    setSettings(prev => ({
      ...prev,
      [method]: { ...prev[method], [key]: value }
    }));
  };

  if (loading) {
    return <Container><p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading payment settings...</p></Container>;
  }

  return (
    <Container>
      {/* Master Toggle */}
      <MasterToggle>
        <ToggleInfo>
          <ToggleTitle>Accept Client Payments</ToggleTitle>
          <ToggleDescription>
            Enable payment requests to collect payments from your clients through iMaven
          </ToggleDescription>
        </ToggleInfo>
        <Toggle>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => updateSettings('enabled', e.target.checked)}
          />
          <span></span>
        </Toggle>
      </MasterToggle>

      {/* PayPal */}
      <PaymentMethodCard $enabled={settings.paypal?.enabled} $disabled={!settings.enabled}>
        <PaymentMethodHeader>
          <PaymentMethodInfo>
            <PaymentMethodIcon $bg="rgba(0, 112, 186, 0.2)" $color="#0070ba">
              <FaPaypal />
            </PaymentMethodIcon>
            <PaymentMethodName>
              <MethodTitle>PayPal</MethodTitle>
              <MethodDescription>Accept payments via PayPal</MethodDescription>
            </PaymentMethodName>
          </PaymentMethodInfo>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.paypal?.enabled}
              onChange={(e) => updatePaymentMethod('paypal', 'enabled', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </PaymentMethodHeader>
        <PaymentMethodBody $show={settings.paypal?.enabled}>
          <InputGroup>
            <Label>PayPal Email or PayPal.me Username</Label>
            <Input
              type="text"
              placeholder="your@email.com or paypal.me/username"
              value={settings.paypal?.paypalEmail || ''}
              onChange={(e) => updatePaymentMethod('paypal', 'paypalEmail', e.target.value)}
            />
            <HelpText>
              We'll generate PayPal.me links for quick payments. Enter your PayPal email or just the username (e.g., "johnsmith" for paypal.me/johnsmith)
            </HelpText>
          </InputGroup>
        </PaymentMethodBody>
      </PaymentMethodCard>

      {/* Bit (Israeli Payment App) */}
      <PaymentMethodCard $enabled={settings.bit?.enabled} $disabled={!settings.enabled}>
        <PaymentMethodHeader>
          <PaymentMethodInfo>
            <PaymentMethodIcon $bg="rgba(46, 204, 113, 0.2)" $color="#2ecc71">
              <FaMobile />
            </PaymentMethodIcon>
            <PaymentMethodName>
              <MethodTitle>Bit</MethodTitle>
              <MethodDescription>Israeli mobile payment app</MethodDescription>
            </PaymentMethodName>
          </PaymentMethodInfo>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.bit?.enabled}
              onChange={(e) => updatePaymentMethod('bit', 'enabled', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </PaymentMethodHeader>
        <PaymentMethodBody $show={settings.bit?.enabled}>
          <InputGroup>
            <Label>Phone Number (linked to Bit)</Label>
            <Input
              type="tel"
              placeholder="+972-50-123-4567"
              value={settings.bit?.phoneNumber || ''}
              onChange={(e) => updatePaymentMethod('bit', 'phoneNumber', e.target.value)}
            />
            <HelpText>
              Enter the phone number linked to your Bit account. Include country code.
            </HelpText>
          </InputGroup>
        </PaymentMethodBody>
      </PaymentMethodCard>

      {/* Bank Transfer */}
      <PaymentMethodCard $enabled={settings.bankTransfer?.enabled} $disabled={!settings.enabled}>
        <PaymentMethodHeader>
          <PaymentMethodInfo>
            <PaymentMethodIcon $bg="rgba(155, 89, 182, 0.2)" $color="#9b59b6">
              <FaUniversity />
            </PaymentMethodIcon>
            <PaymentMethodName>
              <MethodTitle>Bank Transfer</MethodTitle>
              <MethodDescription>Direct wire/bank transfer</MethodDescription>
            </PaymentMethodName>
          </PaymentMethodInfo>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.bankTransfer?.enabled}
              onChange={(e) => updatePaymentMethod('bankTransfer', 'enabled', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </PaymentMethodHeader>
        <PaymentMethodBody $show={settings.bankTransfer?.enabled}>
          <Row>
            <InputGroup>
              <Label>Bank Name</Label>
              <Input
                type="text"
                placeholder="e.g., Bank of America"
                value={settings.bankTransfer?.bankName || ''}
                onChange={(e) => updatePaymentMethod('bankTransfer', 'bankName', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Account Name</Label>
              <Input
                type="text"
                placeholder="Account holder name"
                value={settings.bankTransfer?.accountName || ''}
                onChange={(e) => updatePaymentMethod('bankTransfer', 'accountName', e.target.value)}
              />
            </InputGroup>
          </Row>
          <Row>
            <InputGroup>
              <Label>Account Number / IBAN</Label>
              <Input
                type="text"
                placeholder="Account number or IBAN"
                value={settings.bankTransfer?.accountNumber || ''}
                onChange={(e) => updatePaymentMethod('bankTransfer', 'accountNumber', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Routing Number / Sort Code</Label>
              <Input
                type="text"
                placeholder="Routing or sort code"
                value={settings.bankTransfer?.routingNumber || ''}
                onChange={(e) => updatePaymentMethod('bankTransfer', 'routingNumber', e.target.value)}
              />
            </InputGroup>
          </Row>
          <InputGroup>
            <Label>SWIFT/BIC Code (for international transfers)</Label>
            <Input
              type="text"
              placeholder="e.g., BOFAUS3N"
              value={settings.bankTransfer?.swiftCode || ''}
              onChange={(e) => updatePaymentMethod('bankTransfer', 'swiftCode', e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>Additional Instructions</Label>
            <TextArea
              placeholder="Any special instructions for the transfer (e.g., reference to include)"
              value={settings.bankTransfer?.bankNotes || ''}
              onChange={(e) => updatePaymentMethod('bankTransfer', 'bankNotes', e.target.value)}
            />
          </InputGroup>
        </PaymentMethodBody>
      </PaymentMethodCard>

      {/* Custom Link */}
      <PaymentMethodCard $enabled={settings.customLink?.enabled} $disabled={!settings.enabled}>
        <PaymentMethodHeader>
          <PaymentMethodInfo>
            <PaymentMethodIcon $bg="rgba(241, 196, 15, 0.2)" $color="#f1c40f">
              <FaLink />
            </PaymentMethodIcon>
            <PaymentMethodName>
              <MethodTitle>Custom Payment Link</MethodTitle>
              <MethodDescription>Your own payment page or service</MethodDescription>
            </PaymentMethodName>
          </PaymentMethodInfo>
          <Toggle>
            <input
              type="checkbox"
              checked={settings.customLink?.enabled}
              onChange={(e) => updatePaymentMethod('customLink', 'enabled', e.target.checked)}
            />
            <span></span>
          </Toggle>
        </PaymentMethodHeader>
        <PaymentMethodBody $show={settings.customLink?.enabled}>
          <Row>
            <InputGroup>
              <Label>Button Label</Label>
              <Input
                type="text"
                placeholder="e.g., Pay with Stripe"
                value={settings.customLink?.customLabel || ''}
                onChange={(e) => updatePaymentMethod('customLink', 'customLabel', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Payment URL</Label>
              <Input
                type="url"
                placeholder="https://your-payment-page.com"
                value={settings.customLink?.customUrl || ''}
                onChange={(e) => updatePaymentMethod('customLink', 'customUrl', e.target.value)}
              />
            </InputGroup>
          </Row>
          <HelpText>
            Use this to link to any external payment service like Stripe, Square, or your own payment page.
          </HelpText>
        </PaymentMethodBody>
      </PaymentMethodCard>

      {/* General Settings */}
      <SettingsRow>
        <SettingItem>
          <Label>Default Currency</Label>
          <Select
            value={settings.defaultCurrency}
            onChange={(e) => updateSettings('defaultCurrency', e.target.value)}
            disabled={!settings.enabled}
          >
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="ILS">ILS - Israeli Shekel</option>
            <option value="CAD">CAD - Canadian Dollar</option>
            <option value="AUD">AUD - Australian Dollar</option>
          </Select>
        </SettingItem>
        <SettingItem>
          <Label>Default Expiration (days)</Label>
          <Select
            value={settings.defaultExpirationDays}
            onChange={(e) => updateSettings('defaultExpirationDays', parseInt(e.target.value))}
            disabled={!settings.enabled}
          >
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </Select>
        </SettingItem>
        <SettingItem>
          <Label>Send Payment Reminders</Label>
          <Toggle style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={settings.sendReminders}
              onChange={(e) => updateSettings('sendReminders', e.target.checked)}
              disabled={!settings.enabled}
            />
            <span></span>
          </Toggle>
        </SettingItem>
      </SettingsRow>

      {/* Save Button & Status */}
      {message.text && (
        <StatusMessage $error={message.error}>{message.text}</StatusMessage>
      )}
      <SaveButton onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Payment Settings'}
      </SaveButton>
    </Container>
  );
};

export default PaymentSettings;
